// src/features/radar/BriefCard.jsx
//
// RADAR-BRIEF-STACK: Left-rail brief card for the Decision Brief Stack layout.
//
// Props:
//   item     — radar feed item (itemKey, type, title, dueDate, owner, source, …)
//   selected — boolean; drives accent bar + background fill
//   impact   — seeded integer (10–99) for the IMPACT badge
//   onClick  — selection handler

import React from "react";
import { T } from "../../app/tokens.js";
import { TYPE_META, isSaneDate, daysUntilClient } from "../pulse/LeadershipRadar.jsx";
import { fmtDue, dueColor, whySurfaced } from "./radarUtils.js";

export function BriefCard({ item, selected, impact, onClick }) {
  const meta    = TYPE_META[item.type] ?? TYPE_META.signal;
  const dueStr  = fmtDue(item.dueDate);
  const dueFg   = dueColor(item.dueDate);
  const overdue = isSaneDate(item.dueDate) && daysUntilClient(item.dueDate) < 0;
  const why     = whySurfaced(item);

  return (
    <div
      data-testid={`brief-card-${item.itemKey}`}
      onClick={onClick}
      style={{
        position:    "relative",
        background:  selected ? T.selBg : T.white,
        border:      selected ? `1px solid ${T.selBorder}` : `1px solid ${T.border}`,
        borderLeft:  selected ? `3px solid ${meta.fg}` : `1px solid ${T.border}`,
        borderRadius: T.radiusCard,
        padding:     selected ? "10px 13px 10px 11px" : "10px 13px",
        cursor:      "pointer",
        transition:  "background 0.1s, border-color 0.1s",
        display:     "flex",
        gap:         10,
      }}
    >
      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Eyebrow row: type label + optional source + playbook tag */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <span style={{
            fontSize:      T.fsXxs,
            fontWeight:    T.fwBold,
            color:         meta.fg,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            flexShrink:    0,
          }}>
            {meta.label}
          </span>
          {item.source && (
            <span style={{
              fontSize:  T.fsXxs,
              color:     meta.fg,
              opacity:   0.75,
              flexShrink: 0,
            }}>
              {item.source}
            </span>
          )}
          {item.playbookRunId && (
            <a
              href={`/playbooks/runs/${item.playbookRunId}`}
              data-testid="playbook-source-chip"
              onClick={(e) => e.stopPropagation()}
              style={{
                fontSize:      T.fsXxs,
                fontWeight:    T.fwBold,
                color:         meta.fg,
                background:    "rgba(0,0,0,0.05)",
                borderRadius:  4,
                padding:       "1px 5px",
                textDecoration: "none",
                flexShrink:    0,
              }}
            >
              Playbook →
            </a>
          )}
        </div>

        {/* Title */}
        <div style={{
          fontSize:     T.fsMd,
          fontWeight:   T.fwBold,
          color:        T.dark,
          lineHeight:   1.3,
          marginBottom: 3,
        }}>
          {item.title}
        </div>

        {/* One-line why sentence */}
        <div style={{
          fontSize:     T.fsSm,
          color:        T.mid,
          marginBottom: 5,
          overflow:     "hidden",
          textOverflow: "ellipsis",
          whiteSpace:   "nowrap",
        }}>
          {why}
        </div>

        {/* Meta pill row */}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
          {dueStr !== "—" && (
            <span style={{
              display:      "inline-flex",
              alignItems:   "center",
              gap:          3,
              background:   T.lightGray,
              border:       `1px solid ${T.border}`,
              borderRadius: T.radiusFull,
              padding:      "1px 7px",
              fontSize:     T.fsXxs,
              fontWeight:   T.fwSemibold,
              color:        overdue ? T.radarIssue : dueFg,
              whiteSpace:   "nowrap",
            }}>
              Due {dueStr}
            </span>
          )}
          {item.owner && (
            <span style={{
              display:      "inline-flex",
              alignItems:   "center",
              background:   T.lightGray,
              border:       `1px solid ${T.border}`,
              borderRadius: T.radiusFull,
              padding:      "1px 7px",
              fontSize:     T.fsXxs,
              color:        T.mid,
              whiteSpace:   "nowrap",
              overflow:     "hidden",
              textOverflow: "ellipsis",
              maxWidth:     100,
            }}>
              {item.owner.split(" ")[0]}
            </span>
          )}
          <span style={{
            display:      "inline-flex",
            alignItems:   "center",
            background:   T.blueSoft,
            border:       `1px solid ${T.blueSoftBorder}`,
            borderRadius: T.radiusFull,
            padding:      "1px 7px",
            fontSize:     T.fsXxs,
            fontWeight:   T.fwSemibold,
            color:        T.blue,
            whiteSpace:   "nowrap",
          }}>
            AI surfaced
          </span>
        </div>
      </div>

      {/* IMPACT badge — right side */}
      <div style={{
        display:        "flex",
        flexDirection:  "column",
        alignItems:     "center",
        justifyContent: "center",
        flexShrink:     0,
        minWidth:       40,
      }}>
        <span style={{
          fontSize:           T.fsLg5,
          fontWeight:         T.fwDisplay,
          color:              selected ? meta.fg : T.dark,
          lineHeight:         1,
          fontVariantNumeric: "tabular-nums",
        }}>
          {impact}
        </span>
        <span style={{
          fontSize:      T.fsXxs,
          color:         T.light,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          marginTop:     2,
        }}>
          impact
        </span>
      </div>
    </div>
  );
}
